import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Check, Clock, DollarSign, User, Calendar, Image, ChevronDown, ChevronUp, RefreshCw, Archive, AlertTriangle, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString('es-CO', {
    day: 'numeric', month: 'short', year: 'numeric'
  });
};

const formatCOP = (amount) => {
  if (!amount) return "$0";
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(amount);
};

const getExpiryStatus = (item) => {
  if (item.membershipType === 'lifetime') return { label: 'Lifetime', color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/30', urgent: false };
  if (!item.expiresAt) return { label: 'Sin fecha', color: 'text-white/40', bg: 'bg-white/5 border-white/10', urgent: false };
  
  const now = new Date();
  const expires = new Date(item.expiresAt);
  const daysLeft = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));
  
  if (daysLeft < 0) return { label: `Vencida hace ${Math.abs(daysLeft)}d`, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30', urgent: true };
  if (daysLeft <= 3) return { label: `Vence en ${daysLeft}d`, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30', urgent: true };
  if (daysLeft <= 7) return { label: `Vence en ${daysLeft}d`, color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/30', urgent: true };
  return { label: `${daysLeft}d restantes`, color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/30', urgent: false };
};

const AdminMemberships = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, expiring, expired, lifetime
  const [selectedProof, setSelectedProof] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const AUTH = `Basic ${btoa('admin:business')}`;

  useEffect(() => { fetchRequests(); }, []);

  const fetchRequests = async () => {
    try {
      const response = await fetch("/api/admin/telegram-vip/requests?status=approved", {
        headers: { "Authorization": AUTH }
      });
      if (response.ok) setRequests(await response.json());
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRenew = async (id) => {
    setActionLoading(id + '_renew');
    try {
      const res = await fetch(`/api/admin/telegram-vip/requests/${id}/renew`, {
        method: "PATCH", headers: { "Authorization": AUTH }
      });
      if (res.ok) {
        toast.success("Membresía renovada por 30 días");
        fetchRequests();
      }
    } catch { toast.error("Error al renovar"); }
    finally { setActionLoading(null); }
  };

  const handleArchive = async (id) => {
    setActionLoading(id + '_archive');
    try {
      const res = await fetch(`/api/admin/telegram-vip/requests/${id}/archive`, {
        method: "PATCH", headers: { "Authorization": AUTH }
      });
      if (res.ok) {
        toast.success("Membresía archivada");
        setRequests(prev => prev.filter(r => r.id !== id));
      }
    } catch { toast.error("Error al archivar"); }
    finally { setActionLoading(null); }
  };

  const handleSendRenewal = (item) => {
    const msg = encodeURIComponent(
      `Hola @${item.telegramUsername}! Tu membresía VIP de ${item.creatorName} está por vencer. ¿Deseas renovar? Contáctanos para más info.`
    );
    window.open(`https://t.me/${item.telegramUsername}?text=${msg}`, '_blank');
    toast.success("Abriendo Telegram...");
  };

  // Filter logic
  const now = new Date();
  const filtered = requests.filter(item => {
    if (filter === "all") return true;
    if (filter === "lifetime") return item.membershipType === 'lifetime';
    if (filter === "expired") return item.expiresAt && new Date(item.expiresAt) < now;
    if (filter === "expiring") {
      if (!item.expiresAt || item.membershipType === 'lifetime') return false;
      const daysLeft = Math.ceil((new Date(item.expiresAt) - now) / (1000 * 60 * 60 * 24));
      return daysLeft >= 0 && daysLeft <= 7;
    }
    return true;
  });

  // Sort: urgent first
  const sorted = [...filtered].sort((a, b) => {
    const statusA = getExpiryStatus(a);
    const statusB = getExpiryStatus(b);
    if (statusA.urgent && !statusB.urgent) return -1;
    if (!statusA.urgent && statusB.urgent) return 1;
    return 0;
  });

  // Stats
  const expiring = requests.filter(r => {
    if (!r.expiresAt || r.membershipType === 'lifetime') return false;
    const d = Math.ceil((new Date(r.expiresAt) - now) / (1000 * 60 * 60 * 24));
    return d >= 0 && d <= 7;
  }).length;
  const expired = requests.filter(r => r.expiresAt && new Date(r.expiresAt) < now).length;
  const lifetimeCount = requests.filter(r => r.membershipType === 'lifetime').length;
  const totalUSD = requests.reduce((s, r) => s + (r.priceUSD || 0), 0);

  const filters = [
    { key: "all", label: "Todas", count: requests.length },
    { key: "expiring", label: "Por vencer", count: expiring, color: "text-yellow-400" },
    { key: "expired", label: "Vencidas", count: expired, color: "text-red-400" },
    { key: "lifetime", label: "Lifetime", count: lifetimeCount, color: "text-purple-400" },
  ];

  const handleFixDates = async () => {
    setActionLoading('fix_dates');
    try {
      const res = await fetch("/api/admin/telegram-vip/fix-dates", {
        method: "POST", headers: { "Authorization": AUTH }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        fetchRequests();
      }
    } catch { toast.error("Error"); }
    finally { setActionLoading(null); }
  };

  // Check if any membership is missing dates
  const missingDates = requests.filter(r => r.membershipType === 'monthly' && !r.expiresAt).length;

  return (
    <AdminLayout
      title="Membresías"
      subtitle="Gestión de membresías VIP activas"
      actions={missingDates > 0 ? (
        <Button
          onClick={handleFixDates}
          disabled={actionLoading === 'fix_dates'}
          className="bg-yellow-600 hover:bg-yellow-700 text-white gap-2 text-sm"
          data-testid="fix-dates-btn"
        >
          {actionLoading === 'fix_dates' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
          Asignar fechas ({missingDates})
        </Button>
      ) : null}
    >
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
          <span className="text-white/60 text-xs">Activas</span>
          <p className="text-2xl font-bold text-white" data-testid="stat-active">{requests.length}</p>
        </div>
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
          <span className="text-white/60 text-xs">Por vencer (7d)</span>
          <p className="text-2xl font-bold text-yellow-400" data-testid="stat-expiring">{expiring}</p>
        </div>
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <span className="text-white/60 text-xs">Vencidas</span>
          <p className="text-2xl font-bold text-red-400" data-testid="stat-expired">{expired}</p>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
          <span className="text-white/60 text-xs">Ingresos USD</span>
          <p className="text-2xl font-bold text-white" data-testid="stat-revenue">${totalUSD}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
        {filters.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              filter === f.key
                ? "bg-white/20 text-white"
                : "bg-white/5 text-white/50 hover:bg-white/10"
            }`}
            data-testid={`filter-${f.key}`}
          >
            <span className={f.color}>{f.label}</span>
            <span className="ml-1 text-white/30">({f.count})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-white/50">Cargando...</div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-12">
          <Check className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/50">No hay membresías en esta categoría</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sorted.map((item) => {
            const status = getExpiryStatus(item);
            return (
              <div key={item.id} className={`rounded-xl border p-4 ${status.bg}`} data-testid={`membership-${item.id}`}>
                <div className="flex items-start gap-3">
                  {/* Proof thumbnail */}
                  <button
                    onClick={() => setSelectedProof(item.paymentProof)}
                    className="w-14 h-14 rounded-lg bg-white/10 overflow-hidden flex-shrink-0 hover:ring-2 hover:ring-blue-500"
                  >
                    {item.paymentProof ? (
                      <img src={item.paymentProof} alt="Comprobante" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Image className="w-5 h-5 text-white/30" /></div>
                    )}
                  </button>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-medium">@{item.telegramUsername}</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${status.bg} ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                    <p className="text-white/50 text-sm">{item.creatorName}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-white/40">
                      <span>{item.membershipType === 'monthly' ? 'Mensual' : 'Lifetime'}</span>
                      <span className="text-green-400 font-medium">${item.priceUSD} USD</span>
                      {item.paymentMethod && <span>{item.paymentMethod}</span>}
                      {item.expiresAt && <span>Vence: {formatDate(item.expiresAt)}</span>}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-1 flex-shrink-0">
                    {item.membershipType === 'monthly' && (
                      <>
                        <button
                          onClick={() => handleSendRenewal(item)}
                          className="p-2 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors"
                          title="Enviar mensaje de renovación"
                          data-testid={`send-renewal-${item.id}`}
                        >
                          <Send className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleRenew(item.id)}
                          disabled={actionLoading === item.id + '_renew'}
                          className="p-2 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors"
                          title="Renovar 30 días"
                          data-testid={`renew-${item.id}`}
                        >
                          {actionLoading === item.id + '_renew' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleArchive(item.id)}
                      disabled={actionLoading === item.id + '_archive'}
                      className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                      title="Archivar/Eliminar"
                      data-testid={`archive-${item.id}`}
                    >
                      {actionLoading === item.id + '_archive' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Proof Modal */}
      {selectedProof && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setSelectedProof(null)}>
          <div className="max-w-2xl max-h-[90vh] overflow-auto">
            <img src={selectedProof} alt="Comprobante" className="w-full h-auto rounded-lg" />
          </div>
          <button onClick={() => setSelectedProof(null)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20">
            X
          </button>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminMemberships;
